import { useEffect, useState, useCallback } from 'react';

let initPromise = null;
let branchInstance = null;

/**
 * Ensures the SDK is initialized only once and provides a reliable way to wait for it.
 */
export const getBranch = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (initPromise) return initPromise;

  initPromise = new Promise(async (resolve) => {
    try {
      const branchModule = await import('branch-sdk');
      const instance = branchModule.default || branchModule;
      const key = process.env.NEXT_PUBLIC_BRANCH_SDK_KEY;

      if (!key) {
        console.error('❌ Branch SDK key is missing!');
        resolve(null);
        return;
      }

      instance.init(key, (err) => {
        if (err) {
          console.error('❌ Branch init error:', err);
          resolve(null);
        } else {
          branchInstance = instance;
          resolve(branchInstance);
        }
      });
    } catch (e) {
      console.error('❌ Error loading Branch SDK:', e);
      resolve(null);
    }
  });

  return initPromise;
};

/**
 * Module-specific URLs for web fallback
 */
const MODULE_URLS = {
  post: (itemId) => `https://www.mybridge.me/post/${itemId}`,
  disk: (itemId, itemData) => itemData?.file || `https://www.mybridge.me/profile/disk?folderId=${itemId}`,
  appointment: (itemId, itemData) => itemData?.url || `https://www.mybridge.me/appointment/${itemData?.profile_appointment || ''}/${itemData?.appointment_slug || itemId}`,
  event: (itemId, itemData, itemAction) => `https://www.mybridge.me/myevents/join/${itemId}`,
  meeting: (itemId) => `https://www.mybridge.me/meet/${itemId}`,
};

/**
 * useBranchLink Hook
 * Handles Branch SDK initialization and link creation
 */
export function useBranchLink() {
  const [isReady, setIsReady] = useState(false);

  /**
   * Initialize Branch SDK on client-side only
   */
  useEffect(() => {
    let isMounted = true;
    getBranch().then((instance) => {
      if (instance && isMounted) {
        setIsReady(true);
      }
    });
    return () => { isMounted = false; };
  }, []);

  /**
   * Create a Branch deep link
   */
  const createBranchLink = useCallback(
    async ({ itemType, itemId, itemData = {}, itemAction }) => {
      itemId = String(itemId);
      const instance = await getBranch();
      if (!instance) {
        console.warn('⚠️ Branch SDK could not be initialized. Using fallback.');
        return null;
      }

      return new Promise((resolve, reject) => {
        // Get correct fallback URL based on module type
        const getDesktopUrl = MODULE_URLS[itemType] || (() => 'https://www.mybridge.me');
        const desktopUrl = getDesktopUrl(itemId, itemData, itemAction);

        const linkData = {
          data: {
            // Metadata for SEO and social previews
            $canonical_identifier: `${itemType}/${itemId}`,
            $og_title: `MyBridge ${itemType}`,
            $og_description: `Check out this ${itemType} on MyBridge!`,
            $og_image_url: 'https://www.mybridge.me/assets/share-image.png',

            // Fallback URLs
            $desktop_url: desktopUrl,
            $ios_url: 'https://apps.apple.com/in/app/mybridge-me/id1663029173',
            $android_url:
              'https://play.google.com/store/apps/details?id=com.databridge.mybridge',

            // Custom data to pass into the app
            appData: {
              itemType,
              itemId,
              itemData,
              ...(itemAction !== undefined && { itemAction })
            },
          },
          tags: [itemType, 'share'],
          channel: 'web',
          feature: 'sharing',
          stage: 'new-share',
          campaign: 'mybridge-campaign',
        };

        console.log('🔗 Creating Branch link with data:', linkData);

        if (!instance.link) {
          console.error('❌ branchInstance.link is undefined!');
          reject(new Error('Branch SDK not loaded properly'));
          return;
        }

        // Create the Branch link
        instance.link(linkData, (err, url) => {
          if (err) {
            console.error('❌ Branch link error:', err);
            reject(err);
          } else {
            resolve(url);
          }
        });
      });
    },
    [] // Removed isReady dependency since we await the promise inside
  );

  return { isReady, createBranchLink };
}
